import { MARKS, NODES, type NodeType } from "@repo/fika-blog/src/nodes"
import type { Editor } from "@repo/fika-blog/src/tiptap/core"
import type { NodeSelection } from "@repo/fika-blog/src/tiptap/pm/state"
import { type AccentHue, accentHues } from "@repo/fika-design-system/color"
import type { IconName } from "@repo/fika-design-system/icon"
import { createMemo, Show } from "solid-js"
import { createEditorTransaction, useEditorIsActive } from "solid-tiptap"
import { Mini } from "@/designSystem/components/mini/mini"
import { Tooltip } from "@/designSystem/components/tooltip"
import {
  ParentButton,
  TransformButton,
} from "@/designSystem/patterns/editor-menu"
import { resize } from "../../image-upload"

type Props = {
  editor: Editor
}

type ListType = Extract<NodeType, "bulletList" | "orderedList">

const BULLET_LIST: ListType = "bulletList"
const ORDERED_LIST: ListType = "orderedList"
const NO_HIGHLIGHT = "no-highlight"
const COLOR_ICONS = {
  sand: "wind",
  amber: "citrus",
  indigo: "droplet",
  emerald: "leaf",
  orange: "carrot",
  rose: "cherry",
} as const
const COLOR_LABELS = {
  sand: "grey",
  amber: "yellow",
  indigo: "blue",
  emerald: "green",
  orange: "orange",
  rose: "red",
} as const

export function TextMenu(props: Props) {
  const selection = createEditorTransaction(
    () => props.editor,
    editor => editor.state.selection,
  )

  const hasTextSelection = createEditorTransaction(
    () => props.editor,
    editor => !editor.state.selection.empty,
  )

  function setLink() {
    const previousUrl = props.editor.getAttributes("link").href
    const url = window.prompt("URL", previousUrl)
    if (url === null) return
    if (url === "") {
      props.editor.chain().focus().extendMarkRange("link").unsetLink().run()
      return
    }

    props.editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url })
      .run()
  }

  async function handleImageUpload(file: File) {
    const src = await resize(file)
    props.editor
      .chain()
      .focus()
      .insertContent({
        type: "image",
        attrs: { src },
      })
      .run()
  }

  function match(type: string, attrs?: Record<string, any>) {
    return useEditorIsActive(
      () => props.editor,
      () => type,
      attrs,
    )
  }

  const markValues = createMemo(() => {
    return Object.entries(MARKS)
      .filter(([type]) => match(type)())
      .map(([type]) => type)
  })

  const highlightValue = createMemo(() => {
    if (!match("highlight")()) return undefined
    const color = props.editor.getAttributes("highlight").color as AccentHue
    return color || "amber"
  })

  const linkValue = createMemo(() => {
    if (match("link")()) return "link"
    return null
  })

  const markOptions = createMemo(() => {
    const formatMarks = Object.values(MARKS)
      .filter(mark => mark.group === "format")
      .map(mark => ({
        value: mark.type,
        iconName: mark.iconName,
      }))

    if (match("highlight")() || !hasTextSelection()) {
      return formatMarks.filter(mark => mark.value !== "highlight")
    }

    return formatMarks
  })

  function markOnChange(values: string[]) {
    const formatMarks = Object.values(MARKS)
      .filter(mark => mark.group === "format")
      .map(mark => mark.type)

    for (const mark of formatMarks) {
      if (values.includes(mark)) {
        props.editor.chain().focus().setMark(mark).run()
      } else {
        props.editor.chain().focus().unsetMark(mark).run()
      }
    }
  }

  function highlightOnChange(value: string | null) {
    if (!value || value === NO_HIGHLIGHT) {
      props.editor.chain().focus().unsetMark("highlight").run()
      return
    }

    props.editor
      .chain()
      .focus()
      .setMark("highlight", { color: value as AccentHue })
      .run()
  }

  function linkOnChange(value: string | null) {
    switch (value) {
      case null:
        props.editor.chain().focus().extendMarkRange("link").unsetLink().run()
        return
      case "link":
        return setLink()
    }
  }

  function toggleList(type: ListType) {
    const chain = props.editor.chain().focus()
    if (type === BULLET_LIST) {
      chain.toggleBulletList().run()
    } else {
      chain.toggleOrderedList().run()
    }
  }

  return (
    <Mini.Menu>
      <Mini.Section>
        <ParentButton editor={props.editor} />
        <TransformButton
          editor={props.editor}
          selection={() => selection() as NodeSelection}
        />

        <Mini.Divider />
        <Mini.ToggleGroupMultiple
          value={markValues()}
          onChange={markOnChange}
          values={markOptions()}
        />
        <Show when={match("highlight")() && hasTextSelection()}>
          <Tooltip label="highlight color" hue={highlightValue()}>
            <Mini.Select
              items={[
                {
                  value: NO_HIGHLIGHT,
                  label: "no highlight",
                  iconName: "x" as IconName,
                  hue: "sand",
                },
                ...accentHues.map(value => ({
                  value,
                  label: COLOR_LABELS[value],
                  iconName: COLOR_ICONS[value],
                  hue: value,
                })),
              ]}
              value={highlightValue()}
              onChange={highlightOnChange}
              fixedIcon="highlighter"
              hideValue
            />
          </Tooltip>
        </Show>

        <Show when={hasTextSelection()}>
          <Mini.Divider />
          <Mini.ToggleGroup
            value={linkValue()}
            onChange={linkOnChange}
            values={[
              {
                value: MARKS.link.type,
                iconName: MARKS.link.iconName,
              },
            ]}
          />
        </Show>

        <Mini.Divider />
        <Tooltip label="bullet list">
          <Mini.Button
            iconName={NODES.bulletList.iconName}
            onClick={() => toggleList(BULLET_LIST)}
          />
        </Tooltip>
        <Tooltip label="ordered list">
          <Mini.Button
            iconName={NODES.orderedList.iconName}
            onClick={() => toggleList(ORDERED_LIST)}
          />
        </Tooltip>
        <Mini.ToggleGroup
          value={match("blockquote")() ? "blockquote" : null}
          onChange={() => {
            props.editor.chain().focus().toggleBlockquote().run()
          }}
          values={[
            {
              value: "blockquote",
              iconName: NODES.blockquote.iconName,
            },
          ]}
        />

        <Show when={!hasTextSelection()}>
          <Mini.Divider />
          <Tooltip label="insert image">
            <Mini.ImagePicker
              onChange={handleImageUpload}
              iconName="image"
              title="insert image"
            />
          </Tooltip>
        </Show>
      </Mini.Section>
    </Mini.Menu>
  )
}
